import { Brain, Library, ListChecks, ShieldCheck } from "lucide-react";
import { useTranslation } from "@thyra/landing-i18n";
import type { CatalogAgent } from "../../lib/agent-catalog";

/**
 * The quiet spec line at the foot of a card: safety rules, instructions, memory, knowledge base. Muted icon plus
 * a number or word; one line that never wraps, and a fact that does not apply is left out. Each item carries its
 * full sentence as tooltip and accessible name.
 */
export function HoodRow({ agent }: { agent: CatalogAgent }) {
  const { t } = useTranslation("sections");
  const days = agent.memory?.retentionDays ?? null;
  const items: {
    key: string;
    Icon: typeof ShieldCheck;
    text: string;
    label: string;
  }[] = [];
  if (agent.guardrails > 0) {
    items.push({
      key: "guardrails",
      Icon: ShieldCheck,
      text: String(agent.guardrails),
      label: t("assistantRoles.hood.guardrails", { count: agent.guardrails }),
    });
  }
  if (agent.instructions > 0) {
    items.push({
      key: "instructions",
      Icon: ListChecks,
      text: String(agent.instructions),
      label: t("assistantRoles.hood.instructions", {
        count: agent.instructions,
      }),
    });
  }
  if (agent.memory) {
    items.push({
      key: "memory",
      Icon: Brain,
      text: days
        ? t("assistantRoles.hood.memoryShort", { count: days })
        : t("assistantRoles.hood.memoryWord"),
      label: days
        ? t("assistantRoles.hood.memoryDays", { count: days })
        : t("assistantRoles.hood.memory"),
    });
  }
  if (agent.knowledgeBase) {
    items.push({
      key: "knowledge",
      Icon: Library,
      text: t("assistantRoles.hood.knowledgeWord"),
      label: t("assistantRoles.hood.knowledge"),
    });
  }
  if (items.length === 0) return null;
  return (
    <ul
      aria-label={t("assistantRoles.hood.label")}
      className="relative z-10 mt-auto flex min-w-0 items-center gap-3 overflow-hidden whitespace-nowrap border-t border-border-subtle pt-2.5 text-xs tabular-nums text-muted-foreground"
    >
      {items.map(({ key, Icon, text, label }) => (
        <li key={key} className="inline-flex shrink-0">
          <span
            role="img"
            aria-label={label}
            title={label}
            className="inline-flex items-center gap-1"
          >
            <Icon
              className="h-3.5 w-3.5 shrink-0"
              strokeWidth={2}
              aria-hidden
            />
            {text}
          </span>
        </li>
      ))}
    </ul>
  );
}
